import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const AdvanceEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/advances",
    token
  );

  // Fetch foreign key data: shops, employees, and currencies.
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );
  const { data: employeesData, loading: employeeLoading } = useFetchObjects(
    ["employee"],
    "shop/employees",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies",
    token
  );

  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];
  const employees = Array.isArray(employeesData) ? employeesData : employeesData?.results || [];
  const currencies = Array.isArray(currenciesData) ? currenciesData : currenciesData?.results || [];

  const [selectedShop, setSelectedShop] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [notes, setNotes] = useState("");

  // Set initial values from editData.
  useEffect(() => {
    if (editData) {
      setSelectedEmployee(
        editData.employee && editData.employee.id
          ? String(editData.employee.id)
          : editData.employee
          ? String(editData.employee)
          : ""
      );
      setSelectedCurrency(
        editData.currency && editData.currency.id
          ? String(editData.currency.id)
          : editData.currency
          ? String(editData.currency)
          : ""
      );
      setAmount(editData.amount || "");
      setYear(editData.year || "");
      setMonth(editData.month || "");
      setPaymentDate(
        editData.payment_date ? editData.payment_date.substring(0, 16) : ""
      );
      setNotes(editData.notes || "");
    }
  }, [editData]);

  useEffect(() => {
    if (editData && employees && employees.length > 0) {
      const employeeId =
        editData.employee && editData.employee.id
          ? String(editData.employee.id)
          : editData.employee
          ? String(editData.employee)
          : "";
      const exists = (employees || []).find((e) => String(e.id) === employeeId);
      if (exists) {
        setSelectedEmployee(employeeId);
      } else {
        setSelectedEmployee("");
      }
    }
  }, [editData, employees]);

  useEffect(() => {
    if (editData && currencies && currencies.length > 0) {
      const currencyId =
        editData.currency && editData.currency.id
          ? String(editData.currency.id)
          : editData.currency
          ? String(editData.currency)
          : "";
      const exists = (currencies || []).find(
        (c) => String(c.id) === currencyId
      );
      if (exists) {
        setSelectedCurrency(currencyId);
      } else {
        setSelectedCurrency("");
      }
    }
  }, [editData, currencies]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("employee", selectedEmployee);
    formData.append("currency", selectedCurrency);
    formData.append("amount", amount);
    formData.append("year", year);
    formData.append("month", month);
    formData.append("payment_date", paymentDate);
    formData.append("notes", notes);
    handleUpdate(editData.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      setSelectedEmployee("");
      setSelectedCurrency("");
      setAmount("");
      setYear("");
      setMonth("");
      setPaymentDate("");
      setNotes("");
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("AdvanceIndex.titleEdit")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("AdvanceIndex.employee")}
            fullWidth
            required
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            {employeeLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (employees || []).map((emp) => (
                <MenuItem key={emp.id} value={String(emp.id)}>
                  {emp.name || "--"}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("AdvanceIndex.currency")}
            fullWidth
            required
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (currencies || []).map((cur) => (
                <MenuItem key={cur.id} value={String(cur.id)}>
                  {cur.title || "--"}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            margin="dense"
            label={t("AdvanceIndex.amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <TextField
            select
            margin="dense"
            label={t("AdvanceIndex.year")}
            fullWidth
            required
            value={year}
            onChange={(e) => setYear(e.target.value)}
          >
            {Array.from({length: 31}, (_, i) => 2020 + i).map(yearOption => (
              <MenuItem key={yearOption} value={yearOption}>
                {yearOption}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("AdvanceIndex.month")}
            fullWidth
            required
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {Array.from({length: 12}, (_, i) => i + 1).map(monthOption => (
              <MenuItem key={monthOption} value={monthOption}>
                {new Date(0, monthOption - 1).toLocaleString('default', { month: 'long' })}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label={t("AdvanceIndex.paymentDate")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            margin="dense"
            label={t("AdvanceIndex.notes")}
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("AdvanceIndex.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedEmployee ||
            !selectedCurrency ||
            !amount ||
            !year ||
            !month ||
            !paymentDate
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("AdvanceIndex.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvanceEdit;
